import { useCallback, useEffect, useRef, useState } from 'react';

import { GiftCardsReloadAndPayActionSheet } from '@fhs/payments/src/components/gift-cards-reload-and-pay-action-sheet';
import { useOrderPayment } from '@fhs-legacy/frontend/src/pages/cart/payment/order-payment/use-order-payment';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { CardType } from '@fhs-legacy/frontend/src/state/payment/types';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { mapPaymentMethodCardTypes } from '@fhs-legacy/frontend/src/utils/payment/map-payment-method-card-type';
import { isDelivery } from '@fhs-legacy/frontend/src/utils/service-mode';

import { useGiftCardCheckoutValidation } from '../../../hooks/use-gift-card-checkout-validation';
import { PaymentOrchestrator } from '../../../payment/core/payment-orchestrator';
import useBuildPaymentProcessor from '../../../payment/hooks/use-build-payment-processor';
import { useBuildPipelineContext } from '../../../payment/hooks/use-build-pipeline-context';
import type { PaymentPipelineError, PaymentPipelineResult } from '../../../payment/payment-types';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';
import { usePaymentMethodStore } from '../../../state/payment-stores/payment-method-store';
import { handlePlaceOrder } from '../place-order-button/handle-place-order';

type GiftCardReloadActionSheetWrapperProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function GiftCardReloadActionSheetWrapper({
  isOpen,
  onClose,
}: GiftCardReloadActionSheetWrapperProps) {
  const { selectedGiftCard, giftCardDueAmountInCents, giftCardMissingAmount } =
    useGiftCardCheckoutValidation();
  const { serviceMode, serverOrder, setShouldSkipPriceOrder } = useCartContext();
  const { logRBIEvent } = useCRMEventsContext();
  const processor = useBuildPaymentProcessor();

  // Capture the gift card account ID to restore after reload
  // (UserAccounts refetch may override the payment method selection)
  const selectedGiftCardAccountId = usePaymentMethodStore(
    state => state.selectedGiftCardAccountId
  );

  const [isProcessing, setIsProcessing] = useState(false);

  // TODO: confirm if this will be needed
  const [_currentError, setCurrentError] = useState<PaymentPipelineError | null>(null);

  const lastResultRef = useRef<PaymentPipelineResult | null>(null);
  const orchestratorRef = useRef<PaymentOrchestrator>(new PaymentOrchestrator());
  const { buildPipelineContext } = useBuildPipelineContext();
  const wasProcessingRef = useRef(false);

  const isDeliveryOrder = isDelivery(serviceMode);
  const orderPayment = useOrderPayment({
    serverOrder,
  });

  const {
    paymentMethods,
    paymentValues,
    errors: paymentErrors,
    onChange: handleCreditCardFormChange,
    validateCreditCardForm: handleCreditCardFormSubmit,
  } = orderPayment;

  const rawSupportedCardTypes = useFlag('supported-card-brands-variations');
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const handleGiftCardReloadFinish = useCallback(() => {
    // Restore gift card selection after reload and prevent hooks from overriding it
    // (UserAccounts refetch triggers hooks that may override the payment method)
    // Note: setSelectedGiftCardAccountId clears preservePaymentMethodSelection,
    // so we must set the preserve flag AFTER restoring the gift card selection
    const store = usePaymentMethodStore.getState();
    if (selectedGiftCardAccountId) {
      store.setSelectedGiftCardAccountId(selectedGiftCardAccountId);
    }
    store.setPreservePaymentMethodSelection(true);

    onClose();

    // Set rbiOrderId for routing off cart or checkout page (required for CartCommitLoading navigation)
    if (serverOrder?.rbiOrderId) {
      usePaymentFlowStore.getState().setRbiOrderId(serverOrder.rbiOrderId);
    }

    handlePlaceOrder({
      lastResultRef,
      orchestratorRef,
      buildPipelineContext,
      setIsProcessing,
      setShouldSkipPriceOrder,
      setCurrentError,
      logRBIEvent,
    });
  }, [buildPipelineContext, setShouldSkipPriceOrder, logRBIEvent, serverOrder?.rbiOrderId, selectedGiftCardAccountId, onClose]);

  const handleReloadError = useCallback((error: unknown) => {
    logger.error({
      message: '[GiftCardReload] Reload mutation failed',
      error: error instanceof Error ? error.message : String(error),
    });
    setCurrentError({
      code: 'GIFT_CARD_RELOAD_FAILED',
      message: error instanceof Error ? error.message : 'Unknown error',
      userMessage: 'Gift card reload failed. Please try again or use a different payment method.',
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isProcessing) {
      return;
    }
    onClose();
  }, [isProcessing, onClose]);

  // Close action sheet when processing finishes
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing && isOpen) {
      // Processing has finished, close the action sheet
      wasProcessingRef.current = false;
      onClose();
    }
  }, [isProcessing, isOpen, onClose]);

  const isVisible = isOpen && Boolean(selectedGiftCard) && Boolean(giftCardMissingAmount);

  // Get storeId from server order for vaulting new cards
  const storeId = serverOrder?.cart?.storeDetails?.storeNumber ?? '';

  // Only mount the action sheet when visible - this ensures state is initialized with correct values
  if (!isVisible) {
    return null;
  }

  return (
    <GiftCardsReloadAndPayActionSheet
      headerTitle="Reload Funds"
      isVisible
      onClose={handleClose}
      paymentMethods={paymentMethods}
      amountDueInCents={giftCardDueAmountInCents}
      currentBalanceInCents={selectedGiftCard?.currentBalance ?? 0}
      cardLastDigits={selectedGiftCard?.last4 ?? ''}
      paymentValues={paymentValues}
      supportedCardTypes={supportedCardTypes}
      savePaymentMethodErrors={paymentErrors}
      isDeliveryOrder={isDeliveryOrder}
      onReload={handleGiftCardReloadFinish}
      onReloadError={handleReloadError}
      onCreditCardFormChange={handleCreditCardFormChange}
      onCreditCardFormSubmit={handleCreditCardFormSubmit}
      isPlacingOrder={isProcessing}
      storeId={storeId}
      processor={processor ?? undefined}
    />
  );
}
